/**
 * Reine Helfer für die Inline-TAC-Kalibrierung (Legacy-UX aus app.js portiert).
 * DOM-/render-frei, damit testbar. Alle Koordinaten sind VOLLBILD-normalisiert
 * (0..1), genau wie die gespeicherten `calib.p` und `crop`-Werte.
 */

/** Sichtbarer Bild-Ausschnitt (normalisierte Vollbild-Koordinaten). */
export interface TacViewportRect {
  u0: number;
  u1: number;
  v0: number;
  v1: number;
}

export const CROP_MIN = 0.04;

const clamp01 = (n: number): number => Math.max(0, Math.min(1, n));

/**
 * Inverse zur Viewport-Projektion: Div-Bruchteil (fx,fy ∈ 0..1 des Map-Containers)
 * → Vollbild-uv. `u = u0 + fx·(u1−u0)`, `v = v0 + fy·(v1−v0)`, geklemmt.
 */
export function viewportToImagePoint(fx: number, fy: number, vp: TacViewportRect): { u: number; v: number } {
  const w = vp.u1 - vp.u0;
  const h = vp.v1 - vp.v0;
  return { u: clamp01(vp.u0 + fx * w), v: clamp01(vp.v0 + fy * h) };
}

/**
 * Crop-Rechteck aus zwei Drag-Eckpunkten (Vollbild-uv). Sortiert, auf 4 Dezimal
 * gerundet. Zu kleines Rechteck (< `minSize` in einer Achse) → null (= Reset,
 * wie Legacy `cropUp`).
 */
export function cropFromDrag(
  a: { u: number; v: number },
  b: { u: number; v: number },
  minSize = CROP_MIN,
): TacViewportRect | null {
  const u0 = Math.min(a.u, b.u), u1 = Math.max(a.u, b.u);
  const v0 = Math.min(a.v, b.v), v1 = Math.max(a.v, b.v);
  if (u1 - u0 < minSize || v1 - v0 < minSize) return null;
  const r = (n: number): number => Math.round(n * 1e4) / 1e4;
  return { u0: r(u0), u1: r(u1), v0: r(v0), v1: r(v1) };
}

/**
 * Fit-Crop = Bounding-Box der (Vollbild-uv-)Objektpunkte + Margin, in [0,1]
 * geklemmt (Legacy `tacFit`-Auto-Zoom). Margin = `0.16·max(Δu,Δv) + 0.05`.
 * Zu wenig/zu klein → null (dann normales Verhalten).
 */
export function fitViewportFromImagePoints(pts: { u: number; v: number }[]): TacViewportRect | null {
  if (!Array.isArray(pts) || pts.length === 0) return null;
  let u0 = Infinity, u1 = -Infinity, v0 = Infinity, v1 = -Infinity;
  for (const p of pts) {
    if (!Number.isFinite(p.u) || !Number.isFinite(p.v)) continue;
    if (p.u < u0) u0 = p.u;
    if (p.u > u1) u1 = p.u;
    if (p.v < v0) v0 = p.v;
    if (p.v > v1) v1 = p.v;
  }
  if (!Number.isFinite(u0) || !Number.isFinite(v0)) return null;
  const mg = Math.max(u1 - u0, v1 - v0) * 0.16 + 0.05;
  u0 = Math.max(0, u0 - mg);
  u1 = Math.min(1, u1 + mg);
  v0 = Math.max(0, v0 - mg);
  v1 = Math.min(1, v1 + mg);
  if (u1 - u0 < 0.05 || v1 - v0 < 0.05) return null;
  return { u0, u1, v0, v1 };
}

/** Eine gesetzte Kalibrier-Korrespondenz: Bildpunkt p=[u,v] ↔ Weltpunkt w=[x,z]. */
export interface TacCalibCapture {
  p: [number, number];
  w: [number, number];
}
